import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

// SQL — Supabase'de çalıştırılacak:
// CREATE TABLE IF NOT EXISTS sefer_tahsilat (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   sefer_id uuid REFERENCES seferler(id) ON DELETE CASCADE,
//   depo_id uuid REFERENCES depo(id) ON DELETE CASCADE,
//   tahsil_tutari numeric,
//   notlar text,
//   durum varchar DEFAULT 'Ödenmedi',
//   created_at timestamp DEFAULT now()
// );
// CREATE TABLE IF NOT EXISTS sefer_gider (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   sefer_id uuid REFERENCES seferler(id) ON DELETE CASCADE,
//   aciklama varchar,
//   tutar numeric,
//   created_at timestamp DEFAULT now()
// );
// ALTER TABLE seferler ADD COLUMN IF NOT EXISTS arsivlendi boolean DEFAULT false;
// ALTER TABLE seferler ADD COLUMN IF NOT EXISTS arsiv_tarihi timestamp;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS fatura_bedeli numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS gumruk_yuzdesi numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS gumruk_tutari numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS tasima numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS ic_nakliye numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS izin numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS diger_aciklama varchar;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS diger_tutar numeric;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS fiyat_modeli varchar DEFAULT 'kalemli';
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS para_birimi varchar DEFAULT 'TL';
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS indirim_tipi varchar DEFAULT 'TL';
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS indirim_degeri numeric DEFAULT 0;
// ALTER TABLE sefer_tahsilat ADD COLUMN IF NOT EXISTS indirim_tutari numeric DEFAULT 0;

// ── Yardımcılar ─────────────────────────────────────────────────
const PARA_BIRIMLERI = ['TL', 'USD', 'EUR'];
const PB_SEMBOL = { TL: '₺', USD: '$', EUR: '€' };

const paraFormat = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v)) + ' ₺';
};

const paraFormatBirim = (v, birim = 'TL') => {
  if (v === null || v === undefined || v === '') return '—';
  const sembol = PB_SEMBOL[birim] || '₺';
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v)) + ' ' + sembol;
};

const sayiFormat = (v) =>
  v ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(v)) : '';

const DURUM_RENKLER = {
  'Ödendi':    { bg: '#EAF3DE', renk: '#3B6D11' },
  'Arandı':    { bg: '#FAEEDA', renk: '#854F0B' },
  'Ödenmedi':  { bg: 'transparent', renk: '#555' },
};

const tarihFormat = (t) =>
  t ? new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const GUMRUK_SECENEKLER = [20, 25, 30, 35, 'Serbest'];

// ── Hesaplama formu varsayılan state ────────────────────────────
const bosHesapForm = () => ({
  model: 'kalemli',       // 'kalemli' | 'goturU'
  para_birimi: 'TL',      // 'TL' | 'USD' | 'EUR'
  fatura_bedeli: '',
  gumruk_yuzdesi: 25,
  gumruk_serbest: '',     // model=serbest ise kullanılır
  tasima: '',
  ic_nakliye_aktif: false,
  ic_nakliye: '',
  izin_aktif: false,
  izin: '',
  diger_aktif: false,
  diger_aciklama: '',
  diger_tutar: '',
  goturU_tutar: '',
  goturU_not: '',
  indirim_tipi: 'TL',     // 'TL' | '%'
  indirim_degeri: '',
});

// ── Not özeti oluştur ────────────────────────────────────────────
const ozetOlustur = (form, araToplam, indirimTutar, netToplam) => {
  const s = PB_SEMBOL[form.para_birimi] || '₺';
  const hasIndirim = indirimTutar > 0;
  const indirimLabel = form.indirim_tipi === '%'
    ? `İndirim %${form.indirim_degeri}`
    : 'İndirim';

  if (form.model === 'goturU') {
    const parcalar = [`Götürü: ${sayiFormat(form.goturU_tutar)} ${s}`];
    if (hasIndirim) {
      parcalar.push(`${indirimLabel}: -${sayiFormat(indirimTutar)} ${s}`);
      parcalar.push(`Net: ${sayiFormat(netToplam)} ${s}`);
    }
    if (form.goturU_not.trim()) parcalar.push(form.goturU_not.trim());
    return parcalar.join(' | ');
  }
  const yuzdesi = form.gumruk_yuzdesi === 'Serbest'
    ? parseFloat(form.gumruk_serbest) || 0
    : form.gumruk_yuzdesi;
  const gumrukTutar = ((parseFloat(form.fatura_bedeli) || 0) * yuzdesi) / 100;
  const parcalar = [];
  if (form.fatura_bedeli) parcalar.push(`Fatura: ${sayiFormat(form.fatura_bedeli)} ${s}`);
  if (yuzdesi && form.fatura_bedeli) parcalar.push(`Gümrük %${yuzdesi}: ${sayiFormat(gumrukTutar)} ${s}`);
  if (form.tasima) parcalar.push(`Taşıma: ${sayiFormat(form.tasima)} ${s}`);
  if (form.ic_nakliye_aktif && form.ic_nakliye) parcalar.push(`İç Nakliye: ${sayiFormat(form.ic_nakliye)} ${s}`);
  if (form.izin_aktif && form.izin) parcalar.push(`İzin: ${sayiFormat(form.izin)} ${s}`);
  if (form.diger_aktif && form.diger_tutar) {
    const label = form.diger_aciklama || 'Diğer';
    parcalar.push(`${label}: ${sayiFormat(form.diger_tutar)} ${s}`);
  }
  if (hasIndirim) {
    parcalar.push(`${indirimLabel}: -${sayiFormat(indirimTutar)} ${s}`);
    parcalar.push(`Net: ${sayiFormat(netToplam)} ${s}`);
  } else {
    parcalar.push(`Toplam: ${sayiFormat(netToplam)} ${s}`);
  }
  return parcalar.join(' | ');
};

// ── Hesaplama formu bileşeni ─────────────────────────────────────
function HesaplamaFormu({ tahsilat, onKaydet, onIptal }) {
  const [form, setForm] = useState(() => {
    // Mevcut verilerden doldur
    const t = tahsilat;
    const base = bosHesapForm();
    if (t.fiyat_modeli === 'goturU') {
      return {
        ...base,
        model: 'goturU',
        para_birimi: t.para_birimi || 'TL',
        goturU_tutar: t.tahsil_tutari != null ? (parseFloat(t.tahsil_tutari) + (parseFloat(t.indirim_tutari) || 0)) : '',
        goturU_not: '',
        indirim_tipi: t.indirim_tipi || 'TL',
        indirim_degeri: t.indirim_degeri ?? '',
      };
    }
    return {
      ...base,
      model: 'kalemli',
      para_birimi: t.para_birimi || 'TL',
      fatura_bedeli: t.fatura_bedeli ?? '',
      gumruk_yuzdesi: t.gumruk_yuzdesi != null && !GUMRUK_SECENEKLER.slice(0, -1).includes(t.gumruk_yuzdesi)
        ? 'Serbest' : (t.gumruk_yuzdesi ?? 25),
      gumruk_serbest: (!GUMRUK_SECENEKLER.slice(0, -1).includes(t.gumruk_yuzdesi) && t.gumruk_yuzdesi != null)
        ? t.gumruk_yuzdesi : '',
      tasima: t.tasima ?? '',
      ic_nakliye_aktif: !!(t.ic_nakliye),
      ic_nakliye: t.ic_nakliye ?? '',
      izin_aktif: !!(t.izin),
      izin: t.izin ?? '',
      diger_aktif: !!(t.diger_tutar),
      diger_aciklama: t.diger_aciklama ?? '',
      diger_tutar: t.diger_tutar ?? '',
      indirim_tipi: t.indirim_tipi || 'TL',
      indirim_degeri: t.indirim_degeri ?? '',
    };
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [onUcretNotu, setOnUcretNotu] = useState('');

  useEffect(() => {
    const depoId = tahsilat.depo_id;
    if (!depoId || tahsilat.tahsil_tutari != null) return;
    supabase
      .from('depo')
      .select('on_ucret_turu, on_ucret_tutari, on_ucret_para_birimi, on_ucret_aciklama')
      .eq('id', depoId)
      .eq('on_ucret_var', true)
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        let icNakliye = 0;
        let diger = 0;
        let digerAciklama = '';
        data.forEach(r => {
          const tutar = parseFloat(r.on_ucret_tutari) || 0;
          if (r.on_ucret_turu === 'İç Nakliye') {
            icNakliye += tutar;
          } else {
            diger += tutar;
            digerAciklama = r.on_ucret_turu === 'Diğer' ? (r.on_ucret_aciklama || 'Diğer') : (r.on_ucret_turu || 'Diğer');
          }
        });
        setForm(f => ({
          ...f,
          ...(icNakliye > 0 ? { ic_nakliye_aktif: true, ic_nakliye: String(icNakliye) } : {}),
          ...(diger > 0 ? { diger_aktif: true, diger_tutar: String(diger), diger_aciklama: digerAciklama } : {}),
        }));
        setOnUcretNotu('Depodaki ön ücret bilgileri otomatik yüklendi. Değiştirebilirsiniz.');
      });
  }, []); // eslint-disable-line

  const sembol = PB_SEMBOL[form.para_birimi] || '₺';

  const yuzdesiSayi = form.gumruk_yuzdesi === 'Serbest'
    ? parseFloat(form.gumruk_serbest) || 0
    : form.gumruk_yuzdesi;
  const fatura = parseFloat(form.fatura_bedeli) || 0;
  const gumrukTutar = (fatura * yuzdesiSayi) / 100;

  const araToplam = form.model === 'goturU'
    ? (parseFloat(form.goturU_tutar) || 0)
    : (
      gumrukTutar +
      (parseFloat(form.tasima) || 0) +
      (form.ic_nakliye_aktif ? parseFloat(form.ic_nakliye) || 0 : 0) +
      (form.izin_aktif ? parseFloat(form.izin) || 0 : 0) +
      (form.diger_aktif ? parseFloat(form.diger_tutar) || 0 : 0)
    );

  const indirimDegeri = parseFloat(form.indirim_degeri) || 0;
  const indirimTutar = form.indirim_tipi === '%'
    ? (araToplam * indirimDegeri) / 100
    : indirimDegeri;
  const netToplam = Math.max(0, araToplam - indirimTutar);

  const kaydet = () => {
    const not = ozetOlustur(form, araToplam, indirimTutar, netToplam);
    const gercekYuzde = form.gumruk_yuzdesi === 'Serbest'
      ? parseFloat(form.gumruk_serbest) || null
      : form.gumruk_yuzdesi;
    onKaydet({
      tahsil_tutari: netToplam || null,
      notlar: not,
      fiyat_modeli: form.model,
      para_birimi: form.para_birimi,
      fatura_bedeli: form.model === 'kalemli' ? (parseFloat(form.fatura_bedeli) || null) : null,
      gumruk_yuzdesi: form.model === 'kalemli' ? gercekYuzde : null,
      gumruk_tutari: form.model === 'kalemli' ? (gumrukTutar || null) : null,
      tasima: form.model === 'kalemli' ? (parseFloat(form.tasima) || null) : null,
      ic_nakliye: form.model === 'kalemli' && form.ic_nakliye_aktif ? (parseFloat(form.ic_nakliye) || null) : null,
      izin: form.model === 'kalemli' && form.izin_aktif ? (parseFloat(form.izin) || null) : null,
      diger_aciklama: form.model === 'kalemli' && form.diger_aktif ? (form.diger_aciklama || null) : null,
      diger_tutar: form.model === 'kalemli' && form.diger_aktif ? (parseFloat(form.diger_tutar) || null) : null,
      indirim_tipi: indirimTutar > 0 ? form.indirim_tipi : null,
      indirim_degeri: indirimTutar > 0 ? indirimDegeri : null,
      indirim_tutari: indirimTutar > 0 ? indirimTutar : null,
    });
  };

  const inputSt = { padding: '6px 9px', border: '1px solid #d0d9e8', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', background: '#fff' };
  const labelSt = { fontSize: '11px', color: '#666', fontWeight: '600', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' };
  const fieldSt = { display: 'flex', flexDirection: 'column', gap: '2px' };

  return (
    <div style={{ background: '#F8FBFF', border: '1.5px solid #c8ddf5', borderRadius: '10px', padding: '16px 18px', marginTop: '4px' }}>

      {/* Model toggle + Para Birimi */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
        {[['kalemli', '📋 Kalemli'], ['goturU', '💰 Götürü']].map(([k, l]) => (
          <button key={k} onClick={() => set('model', k)} style={{
            padding: '5px 14px', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: form.model === k ? '700' : '500',
            background: form.model === k ? '#0D3B7A' : '#e8eef5', color: form.model === k ? '#fff' : '#555',
          }}>{l}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', color: '#888', fontWeight: '600' }}>Para Birimi:</span>
          <select
            value={form.para_birimi}
            onChange={e => set('para_birimi', e.target.value)}
            style={{ padding: '4px 8px', border: '1px solid #c8d8ea', borderRadius: '6px', fontSize: '13px', background: '#fff', fontWeight: '700', color: '#0D3B7A', cursor: 'pointer' }}
          >
            {PARA_BIRIMLERI.map(pb => (
              <option key={pb} value={pb}>{pb === 'TL' ? '₺ TL' : pb === 'USD' ? '$ USD' : '€ EUR'}</option>
            ))}
          </select>
        </div>
      </div>

      {onUcretNotu && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#EAF3DE', border: '1px solid #bde09a', borderRadius: '7px', fontSize: '12px', color: '#3B6D11', display: 'flex', alignItems: 'center', gap: '6px' }}>
          ℹ️ {onUcretNotu}
        </div>
      )}

      {form.model === 'kalemli' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

          {/* Satır 1: Fatura + Gümrük */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ ...fieldSt, flex: '1', minWidth: '140px' }}>
              <div style={labelSt}>Fatura Bedeli ({sembol})</div>
              <input
                type="number"
                value={form.fatura_bedeli}
                onChange={e => set('fatura_bedeli', e.target.value)}
                placeholder="0"
                style={inputSt}
              />
            </div>
            <div style={{ ...fieldSt, flex: '1', minWidth: '160px' }}>
              <div style={labelSt}>Gümrük %</div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select
                  value={form.gumruk_yuzdesi}
                  onChange={e => set('gumruk_yuzdesi', e.target.value === 'Serbest' ? 'Serbest' : Number(e.target.value))}
                  style={{ ...inputSt, width: 'auto', flex: 1 }}
                >
                  {GUMRUK_SECENEKLER.map(v => (
                    <option key={v} value={v}>%{v}</option>
                  ))}
                </select>
                {form.gumruk_yuzdesi === 'Serbest' && (
                  <input
                    type="number"
                    value={form.gumruk_serbest}
                    onChange={e => set('gumruk_serbest', e.target.value)}
                    placeholder="%"
                    style={{ ...inputSt, width: '60px' }}
                  />
                )}
              </div>
              {fatura > 0 && yuzdesiSayi > 0 && (
                <div style={{ fontSize: '11px', color: '#0D3B7A', marginTop: '2px', fontWeight: '600' }}>
                  = {sayiFormat(gumrukTutar)} {sembol}
                </div>
              )}
            </div>
            <div style={{ ...fieldSt, flex: '1', minWidth: '140px' }}>
              <div style={labelSt}>Taşıma ({sembol})</div>
              <input
                type="number"
                value={form.tasima}
                onChange={e => set('tasima', e.target.value)}
                placeholder="0"
                style={inputSt}
              />
            </div>
          </div>

          {/* Satır 2: Opsiyoneller */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {/* İç Nakliye */}
            <div style={{ ...fieldSt, flex: '1', minWidth: '140px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', ...labelSt, marginBottom: '4px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.ic_nakliye_aktif} onChange={e => set('ic_nakliye_aktif', e.target.checked)} />
                İç Nakliye ({sembol})
              </label>
              <input
                type="number"
                value={form.ic_nakliye}
                onChange={e => set('ic_nakliye', e.target.value)}
                placeholder="0"
                disabled={!form.ic_nakliye_aktif}
                style={{ ...inputSt, opacity: form.ic_nakliye_aktif ? 1 : 0.4 }}
              />
            </div>

            {/* İzin */}
            <div style={{ ...fieldSt, flex: '1', minWidth: '140px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', ...labelSt, marginBottom: '4px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.izin_aktif} onChange={e => set('izin_aktif', e.target.checked)} />
                İzin ({sembol})
              </label>
              <input
                type="number"
                value={form.izin}
                onChange={e => set('izin', e.target.value)}
                placeholder="0"
                disabled={!form.izin_aktif}
                style={{ ...inputSt, opacity: form.izin_aktif ? 1 : 0.4 }}
              />
            </div>

            {/* Diğer */}
            <div style={{ ...fieldSt, flex: '2', minWidth: '200px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', ...labelSt, marginBottom: '4px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.diger_aktif} onChange={e => set('diger_aktif', e.target.checked)} />
                Diğer
              </label>
              <div style={{ display: 'flex', gap: '6px', opacity: form.diger_aktif ? 1 : 0.4 }}>
                <input
                  value={form.diger_aciklama}
                  onChange={e => set('diger_aciklama', e.target.value)}
                  placeholder="Açıklama"
                  disabled={!form.diger_aktif}
                  style={{ ...inputSt, flex: 2 }}
                />
                <input
                  type="number"
                  value={form.diger_tutar}
                  onChange={e => set('diger_tutar', e.target.value)}
                  placeholder={sembol}
                  disabled={!form.diger_aktif}
                  style={{ ...inputSt, flex: 1 }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {form.model === 'goturU' && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ ...fieldSt, flex: '1', minWidth: '160px' }}>
            <div style={labelSt}>Götürü Tutar ({sembol})</div>
            <input
              autoFocus
              type="number"
              value={form.goturU_tutar}
              onChange={e => set('goturU_tutar', e.target.value)}
              placeholder="0"
              style={inputSt}
            />
          </div>
          <div style={{ ...fieldSt, flex: '2', minWidth: '200px' }}>
            <div style={labelSt}>Not (opsiyonel)</div>
            <input
              value={form.goturU_not}
              onChange={e => set('goturU_not', e.target.value)}
              placeholder="Ek açıklama..."
              style={inputSt}
            />
          </div>
        </div>
      )}

      {/* İndirim */}
      <div style={{ marginTop: '12px', padding: '10px 12px', background: '#fff8f0', border: '1px solid #f0d9bb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>İndirim</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['TL', '%'].map(t => (
            <button key={t} onClick={() => set('indirim_tipi', t)} style={{
              padding: '3px 10px', border: 'none', borderRadius: '5px', fontSize: '12px', cursor: 'pointer', fontWeight: form.indirim_tipi === t ? '700' : '500',
              background: form.indirim_tipi === t ? '#c45a00' : '#eeddd0', color: form.indirim_tipi === t ? '#fff' : '#8b4a1a',
            }}>{t}</button>
          ))}
        </div>
        <input
          type="number"
          value={form.indirim_degeri}
          onChange={e => set('indirim_degeri', e.target.value)}
          placeholder={form.indirim_tipi === '%' ? '% oran' : `${sembol} tutar`}
          style={{ padding: '5px 8px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '13px', width: '110px', background: '#fff' }}
        />
        {indirimTutar > 0 && (
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#c45a00' }}>
            = -{sayiFormat(indirimTutar)} {sembol}
          </span>
        )}
      </div>

      {/* Toplam + butonlar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #dde8f5', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {indirimTutar > 0 ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#aaa' }}>ARA TOPLAM</span>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#888', textDecoration: 'line-through' }}>
                  {sayiFormat(araToplam)} {sembol}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#c45a00', fontWeight: '700' }}>NET TOPLAM</span>
                <span style={{ fontSize: '20px', fontWeight: '800', color: '#0D3B7A' }}>
                  {sayiFormat(netToplam)} {sembol}
                </span>
              </div>
            </>
          ) : (
            <>
              <span style={{ fontSize: '12px', color: '#888' }}>TOPLAM</span>
              <span style={{ fontSize: '20px', fontWeight: '800', color: araToplam > 0 ? '#0D3B7A' : '#bbb' }}>
                {araToplam > 0 ? `${sayiFormat(araToplam)} ${sembol}` : '—'}
              </span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onIptal} style={{ background: '#eee', color: '#555', border: 'none', padding: '7px 16px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}>İptal</button>
          <button onClick={kaydet} style={{ background: '#0D3B7A', color: '#fff', border: 'none', padding: '7px 20px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer', fontWeight: '700' }}>Kaydet</button>
        </div>
      </div>
    </div>
  );
}

export default function Odemeler() {
  const kullanici = JSON.parse(localStorage.getItem('kullanici') || '{}');
  const kullaniciRol = kullanici.rol || '';
  const yetkili = ['yonetici', 'muhasebeci'].includes(kullaniciRol);

  // Ana sekme
  const [anaSecme, setAnaSecme] = useState('aktif');

  // Aktif seferler
  const [seferler, setSeferler] = useState([]);
  const [secilenSeferId, setSecilenSeferId] = useState('');
  const [secilenSefer, setSecilenSefer] = useState(null);
  const [sekme, setSekme] = useState('tahsilat');
  const [tahsilatlar, setTahsilatlar] = useState([]);
  const [giderler, setGiderler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [acikHesapId, setAcikHesapId] = useState(null); // hangi satırın hesap formu açık
  const [giderFormAcik, setGiderFormAcik] = useState(false);
  const [giderForm, setGiderForm] = useState({ aciklama: '', tutar: '' });
  const [kopyalandi, setKopyalandi] = useState(null);
  const [musteriModal, setMusteriModal] = useState(null); // { ad, kalemler }
  const [hoveredGrup, setHoveredGrup] = useState(null);
  const [modalKopyalandi, setModalKopyalandi] = useState(false);

  // Arşiv
  const [arsivArama, setArsivArama] = useState('');
  const [arsivOzet, setArsivOzet] = useState({});
  const [arsivYukleniyor, setArsivYukleniyor] = useState(false);
  const [arsivDetayModal, setArsivDetayModal] = useState(null);
  const [arsivDetayTahsilat, setArsivDetayTahsilat] = useState([]);
  const [arsivDetayGider, setArsivDetayGider] = useState([]);
  const [arsivDetayYukleniyor, setArsivDetayYukleniyor] = useState(false);

  // ── Seferler ──────────────────────────────────────────────────
  useEffect(() => {
    supabase
      .from('seferler')
      .select('id, sefer_no, guzergah, durum, arsivlendi, arsiv_tarihi')
      .order('created_at', { ascending: false })
      .then(({ data }) => setSeferler(data || []));
  }, []);

  const aktifSeferler = seferler.filter(s => !s.arsivlendi);
  const arsivSeferler = seferler.filter(s => s.arsivlendi);

  // ── Arşiv özetleri ────────────────────────────────────────────
  useEffect(() => {
    if (anaSecme !== 'arsiv' || arsivSeferler.length === 0) return;
    const arsivIds = arsivSeferler.map(s => s.id);
    setArsivYukleniyor(true);
    Promise.all([
      supabase.from('sefer_tahsilat').select('sefer_id, tahsil_tutari, durum').in('sefer_id', arsivIds),
      supabase.from('sefer_gider').select('sefer_id, tutar').in('sefer_id', arsivIds),
    ]).then(([{ data: tData }, { data: gData }]) => {
      const ozet = {};
      arsivIds.forEach(id => { ozet[id] = { tahsilat: 0, gider: 0 }; });
      (tData || []).forEach(t => {
        if (t.durum === 'Ödendi' && ozet[t.sefer_id]) ozet[t.sefer_id].tahsilat += parseFloat(t.tahsil_tutari) || 0;
      });
      (gData || []).forEach(g => {
        if (ozet[g.sefer_id]) ozet[g.sefer_id].gider += parseFloat(g.tutar) || 0;
      });
      setArsivOzet(ozet);
      setArsivYukleniyor(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anaSecme, seferler]);

  // ── Arşiv detay ───────────────────────────────────────────────
  const arsivDetayGor = async (sefer) => {
    setArsivDetayModal(sefer);
    setArsivDetayTahsilat([]);
    setArsivDetayGider([]);
    setArsivDetayYukleniyor(true);
    const [{ data: depoMallari }, { data: tahsilatData }, { data: giderData }] = await Promise.all([
      supabase.from('depo').select('id, gonderici_firma, urun, miktar, fatura_no, musteriler(ad)').eq('sefer_id', sefer.id).order('created_at', { ascending: true }),
      supabase.from('sefer_tahsilat').select('*').eq('sefer_id', sefer.id),
      supabase.from('sefer_gider').select('*').eq('sefer_id', sefer.id).order('created_at', { ascending: true }),
    ]);
    const mallar = depoMallari || [];
    const mevcutMap = {};
    (tahsilatData || []).forEach(t => { mevcutMap[t.depo_id] = t; });
    setArsivDetayTahsilat(mallar.map(m => ({ ...mevcutMap[m.id], _depo: m })).filter(r => r.id));
    setArsivDetayGider(giderData || []);
    setArsivDetayYukleniyor(false);
  };

  const arsivdenCikar = async (seferId) => {
    if (!window.confirm('Bu sefer arşivden çıkarılacak ve aktif seferler listesine dönecek. Emin misiniz?')) return;
    await supabase.from('seferler').update({ arsivlendi: false, arsiv_tarihi: null }).eq('id', seferId);
    setSeferler(prev => prev.map(s => s.id === seferId ? { ...s, arsivlendi: false, arsiv_tarihi: null } : s));
    setArsivDetayModal(null);
  };

  // ── Sefer seç ─────────────────────────────────────────────────
  const seferSec = async (sid) => {
    setSecilenSeferId(sid);
    setSecilenSefer(seferler.find(s => s.id === sid) || null);
    setTahsilatlar([]);
    setGiderler([]);
    setAcikHesapId(null);
    if (!sid) return;
    setYukleniyor(true);

    const { data: depoMallari } = await supabase
      .from('depo')
      .select('id, musteri_id, gonderici_firma, urun, miktar, fatura_no, musteriler(ad)')
      .eq('sefer_id', sid)
      .order('created_at', { ascending: true });

    const mallar = depoMallari || [];

    const { data: mevcutTahsilat } = await supabase
      .from('sefer_tahsilat')
      .select('*')
      .eq('sefer_id', sid);

    const mevcutMap = {};
    (mevcutTahsilat || []).forEach(t => { mevcutMap[t.depo_id] = t; });

    const eksikMallar = mallar.filter(m => !mevcutMap[m.id]);
    if (eksikMallar.length > 0) {
      const { data: eklendi } = await supabase
        .from('sefer_tahsilat')
        .insert(eksikMallar.map(m => ({ sefer_id: sid, depo_id: m.id, tahsil_tutari: null, durum: 'Ödenmedi' })))
        .select('*');
      (eklendi || []).forEach(t => { mevcutMap[t.depo_id] = t; });
    }

    setTahsilatlar(mallar.map(m => ({ ...mevcutMap[m.id], _depo: m })).filter(r => r.id));

    const { data: giderData } = await supabase
      .from('sefer_gider').select('*').eq('sefer_id', sid).order('created_at', { ascending: true });
    setGiderler(giderData || []);
    setYukleniyor(false);
  };

  // ── Tahsilat güncelle ──────────────────────────────────────────
  const tahsilatGuncelle = useCallback(async (tahsilatId, alanlar) => {
    const { data, error } = await supabase
      .from('sefer_tahsilat')
      .update(alanlar)
      .eq('id', tahsilatId)
      .select('*')
      .single();

    if (error) {
      // PGRST116 = satır bulunamadı → upsert dene
      if (error.code === 'PGRST116') {
        const tahsilatMevcut = tahsilatlar.find(t => t.id === tahsilatId);
        if (tahsilatMevcut?.sefer_id && tahsilatMevcut?.depo_id) {
          const { data: uData, error: uError } = await supabase
            .from('sefer_tahsilat')
            .upsert(
              { id: tahsilatId, sefer_id: tahsilatMevcut.sefer_id, depo_id: tahsilatMevcut.depo_id, durum: tahsilatMevcut.durum || 'Ödenmedi', ...alanlar },
              { onConflict: 'id' }
            )
            .select('*')
            .single();
          if (uError) {
            console.error('tahsilatGuncelle upsert hatası:', uError);
            alert('Kayıt kaydedilemedi: ' + uError.message);
            return false;
          }
          if (uData) {
            setTahsilatlar(prev => prev.map(t => t.id === tahsilatId ? { ...t, ...uData } : t));
          }
          return true;
        }
      }
      console.error('tahsilatGuncelle hatası:', error);
      alert('Kayıt güncellenemedi: ' + error.message);
      return false;
    }

    if (data) {
      setTahsilatlar(prev => prev.map(t =>
        t.id === tahsilatId ? { ...t, ...data } : t
      ));
    }
    return true;
  }, [tahsilatlar]);

  const durumDegistir = (tahsilatId, yeniDurum) => {
    tahsilatGuncelle(tahsilatId, { durum: yeniDurum });
  };

  // ── Hesaplama formu kaydet ─────────────────────────────────────
  const hesapKaydet = useCallback(async (tahsilatId, alanlar) => {
    if (!tahsilatId) {
      console.error('hesapKaydet: tahsilatId yok');
      alert('Tahsilat kaydı bulunamadı. Lütfen sayfayı yenileyin.');
      return;
    }
    const ok = await tahsilatGuncelle(tahsilatId, alanlar);
    if (ok) setAcikHesapId(null);
  }, [tahsilatGuncelle]);

  // ── Kopyala ───────────────────────────────────────────────────
  const notKopyala = (id, metin) => {
    if (!metin) return;
    navigator.clipboard.writeText(metin).then(() => {
      setKopyalandi(id);
      setTimeout(() => setKopyalandi(null), 2000);
    });
  };

  // ── Gider ─────────────────────────────────────────────────────
  const giderEkle = async () => {
    if (!giderForm.aciklama || !giderForm.tutar) return alert('Açıklama ve tutar zorunlu!');
    const { data } = await supabase
      .from('sefer_gider')
      .insert([{ sefer_id: secilenSeferId, aciklama: giderForm.aciklama, tutar: parseFloat(giderForm.tutar) }])
      .select('*').single();
    if (data) setGiderler(prev => [...prev, data]);
    setGiderForm({ aciklama: '', tutar: '' });
    setGiderFormAcik(false);
  };

  const giderSil = async (id) => {
    if (!window.confirm('Bu gideri silmek istediğinize emin misiniz?')) return;
    await supabase.from('sefer_gider').delete().eq('id', id);
    setGiderler(prev => prev.filter(g => g.id !== id));
  };

  // ── Arşivle ───────────────────────────────────────────────────
  const arsivle = async () => {
    if (!window.confirm(`"${secilenSefer?.sefer_no}" seferi arşive taşınacak. Emin misiniz?`)) return;
    await supabase
      .from('seferler')
      .update({ arsivlendi: true, arsiv_tarihi: new Date().toISOString() })
      .eq('id', secilenSeferId);
    setSeferler(prev => prev.map(s =>
      s.id === secilenSeferId ? { ...s, arsivlendi: true, arsiv_tarihi: new Date().toISOString() } : s
    ));
    setSecilenSeferId('');
    setSecilenSefer(null);
    setTahsilatlar([]);
    setGiderler([]);
  };

  // ── Özet hesapla ──────────────────────────────────────────────
  const odenmemisCount = tahsilatlar.filter(t => t.durum !== 'Ödendi').length;
  const arsivlenebilir = tahsilatlar.length > 0 && odenmemisCount === 0;
  const toplamGider    = giderler.reduce((s, g) => s + (parseFloat(g.tutar) || 0), 0);

  // Para birimi grupları
  const pbGruplar = {};
  tahsilatlar.forEach(t => {
    const pb = t.para_birimi || 'TL';
    if (!pbGruplar[pb]) pbGruplar[pb] = { alacak: 0, odendi: 0 };
    pbGruplar[pb].alacak += parseFloat(t.tahsil_tutari) || 0;
    if (t.durum === 'Ödendi') pbGruplar[pb].odendi += parseFloat(t.tahsil_tutari) || 0;
  });
  const pbSirali = PARA_BIRIMLERI.filter(pb => pbGruplar[pb]);

  // TL-only özet (NET hesabı için)
  const tlGrup = pbGruplar['TL'] || { alacak: 0, odendi: 0 };
  const tekParaBirimi = pbSirali.length === 1;

  // Toplam indirim (para birimi bazında)
  const indirimGruplar = {};
  tahsilatlar.forEach(t => {
    if (!(parseFloat(t.indirim_tutari) > 0)) return;
    const pb = t.para_birimi || 'TL';
    if (!indirimGruplar[pb]) indirimGruplar[pb] = 0;
    indirimGruplar[pb] += parseFloat(t.indirim_tutari);
  });
  const indirimPbSirali = PARA_BIRIMLERI.filter(pb => indirimGruplar[pb]);

  const filtreliArsiv = arsivSeferler.filter(s => {
    if (!arsivArama.trim()) return true;
    const ara = arsivArama.toLowerCase();
    return s.sefer_no?.toLowerCase().includes(ara) || s.guzergah?.toLowerCase().includes(ara);
  });

  if (!yetkili) {
    return <div style={{ padding: '60px', textAlign: 'center', color: '#888', fontSize: '16px' }}>Bu sayfaya erişim yetkiniz yok.</div>;
  }

  return (
    <div style={{ flex: 1, background: '#f5f5f5', minHeight: '100vh', fontFamily: 'Arial, sans-serif' }}>

      {/* ── Üst bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '14px 28px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#0D3B7A', marginBottom: '12px' }}>Ödemeler</div>
        <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #E3F0FF' }}>
          {[['aktif', '💰 Aktif Seferler'], ['arsiv', '🗄 Arşiv']].map(([k, l]) => (
            <button key={k} onClick={() => setAnaSecme(k)} style={{
              padding: '8px 20px', border: 'none', cursor: 'pointer',
              fontWeight: anaSecme === k ? '700' : '500', fontSize: '14px',
              background: 'none', color: anaSecme === k ? '#0D3B7A' : '#888',
              borderBottom: anaSecme === k ? '3px solid #0D3B7A' : '3px solid transparent',
              marginBottom: '-2px', transition: 'all 0.15s',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* ══ AKTİF SEFERLER ══ */}
      {anaSecme === 'aktif' && (
        <>
          <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '12px 28px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <select
              value={secilenSeferId}
              onChange={e => seferSec(e.target.value)}
              style={{ padding: '8px 14px', border: '1px solid #c8ddf5', borderRadius: '8px', fontSize: '14px', color: '#333', background: '#F8FBFF', minWidth: '280px', fontWeight: '500' }}
            >
              <option value="">— Sefer seçin —</option>
              {aktifSeferler.map(s => (
                <option key={s.id} value={s.id}>{s.sefer_no} · {s.guzergah} · {s.durum}</option>
              ))}
            </select>
            {secilenSefer && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                <span style={{ fontSize: '13px', color: '#888' }}>{secilenSefer.guzergah}</span>
                <span style={{ background: '#E3F0FF', color: '#0D3B7A', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{secilenSefer.durum}</span>
              </div>
            )}
          </div>

          {!secilenSeferId ? (
            <div style={{ padding: '80px', textAlign: 'center', color: '#aaa', fontSize: '15px' }}>Başlamak için bir sefer seçin.</div>
          ) : yukleniyor ? (
            <div style={{ padding: '80px', textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
          ) : (
            <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Sekmeler */}
              <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #E3F0FF' }}>
                {[['tahsilat', '💰 Tahsilat'], ['gider', '📤 Giderler']].map(([k, l]) => (
                  <button key={k} onClick={() => setSekme(k)} style={{
                    padding: '9px 22px', border: 'none', cursor: 'pointer',
                    fontWeight: sekme === k ? '700' : '500', fontSize: '14px',
                    background: 'none', color: sekme === k ? '#0D3B7A' : '#888',
                    borderBottom: sekme === k ? '3px solid #0D3B7A' : '3px solid transparent',
                    marginBottom: '-2px', transition: 'all 0.15s',
                  }}>{l}</button>
                ))}
              </div>

              {/* ══ TAHSİLAT ══ */}
              {sekme === 'tahsilat' && (() => {
                const musteriGruplari = (() => {
                  const map = {};
                  tahsilatlar.forEach(t => {
                    const ad = t._depo?.musteriler?.ad || '—';
                    if (!map[ad]) map[ad] = [];
                    map[ad].push(t);
                  });
                  return Object.entries(map)
                    .map(([ad, kalemler]) => ({ ad, kalemler }))
                    .sort((a, b) => a.ad.localeCompare(b.ad, 'tr'));
                })();
                let globalIdx = 0;
                return (
                  <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #eee', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#F0F6FF' }}>
                          {['#', 'Gönderici', 'Ürün / Miktar', 'Fatura No', 'Tahsil Tutarı', 'Not', 'Durum', ''].map((h, i) => (
                            <th key={i} style={{ padding: '11px 12px', textAlign: 'left', color: '#555', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid #E3F0FF', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tahsilatlar.length === 0 ? (
                          <tr>
                            <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>Bu sefere ait mal kaydı yok.</td>
                          </tr>
                        ) : musteriGruplari.map(grup => (
                          <React.Fragment key={grup.ad}>
                            {/* Grup başlığı */}
                            <tr
                              onMouseEnter={() => setHoveredGrup(grup.ad)}
                              onMouseLeave={() => setHoveredGrup(null)}
                              onClick={() => setMusteriModal({ ad: grup.ad, kalemler: grup.kalemler })}
                              style={{ background: hoveredGrup === grup.ad ? '#d4e4f7' : '#E8F0FB', cursor: 'pointer', borderTop: '2px solid #C8D8F0' }}
                            >
                              <td colSpan={8} style={{ padding: '8px 14px' }}>
                                <span style={{ fontWeight: '700', fontSize: '13px', color: '#0D3B7A' }}>{grup.ad}</span>
                                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#5580B0', fontWeight: '500' }}>({grup.kalemler.length} kalem)</span>
                                <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9abbe0' }}>→ tıkla</span>
                              </td>
                            </tr>
                            {/* Grup kalemleri */}
                            {grup.kalemler.map(t => {
                              const { bg, renk } = DURUM_RENKLER[t.durum] || DURUM_RENKLER['Ödenmedi'];
                              const hesapAcik = acikHesapId === t.id;
                              globalIdx++;
                              return (
                                <React.Fragment key={t.id}>
                                  <tr style={{ background: bg, borderTop: '1px solid #eee' }}>
                                    <td style={{ padding: '10px 12px', color: '#999', fontSize: '12px' }}>{globalIdx}</td>
                                    <td style={{ padding: '10px 12px', color: '#555', fontSize: '12px' }}>{t._depo?.gonderici_firma || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#555' }}>
                                      <div>{t._depo?.urun || '—'}</div>
                                      {t._depo?.miktar && <div style={{ fontSize: '11px', color: '#888' }}>{t._depo.miktar}</div>}
                                    </td>
                                    <td style={{ padding: '10px 12px', color: '#888', fontSize: '12px' }}>{t._depo?.fatura_no || '—'}</td>

                                    {/* Tahsil Tutarı */}
                                    <td style={{ padding: '10px 12px', minWidth: '130px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontWeight: '700', color: t.tahsil_tutari ? '#0D3B7A' : '#bbb', fontSize: t.tahsil_tutari ? '14px' : '13px' }}>
                                          {t.tahsil_tutari != null ? paraFormatBirim(t.tahsil_tutari, t.para_birimi) : '—'}
                                        </span>
                                        <button
                                          onClick={() => setAcikHesapId(hesapAcik ? null : t.id)}
                                          style={{
                                            background: hesapAcik ? '#0D3B7A' : '#E3F0FF',
                                            color: hesapAcik ? '#fff' : '#0D3B7A',
                                            border: 'none', padding: '3px 9px', borderRadius: '5px',
                                            fontSize: '11px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {hesapAcik ? '✕' : '⚡ Hesapla'}
                                        </button>
                                      </div>
                                    </td>

                                    {/* Not */}
                                    <td style={{ padding: '10px 12px', maxWidth: '200px' }}>
                                      {t.notlar ? (
                                        <div style={{ fontSize: '11px', color: '#555', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{t.notlar}</div>
                                      ) : (
                                        <span style={{ color: '#ccc', fontSize: '11px' }}>—</span>
                                      )}
                                    </td>

                                    {/* Durum */}
                                    <td style={{ padding: '10px 12px' }}>
                                      <select
                                        value={t.durum}
                                        onChange={e => durumDegistir(t.id, e.target.value)}
                                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', background: bg, color: renk, fontWeight: '600', cursor: 'pointer' }}
                                      >
                                        <option value="Ödenmedi">Ödenmedi</option>
                                        <option value="Arandı">Arandı</option>
                                        <option value="Ödendi">Ödendi</option>
                                      </select>
                                    </td>

                                    {/* Kopyala */}
                                    <td style={{ padding: '10px 8px' }}>
                                      {t.notlar && (
                                        <button
                                          onClick={() => notKopyala(t.id, t.notlar)}
                                          title="Notu kopyala"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: kopyalandi === t.id ? '#3B6D11' : '#aaa', whiteSpace: 'nowrap', padding: '2px 4px' }}
                                        >
                                          {kopyalandi === t.id ? '✓' : '📋'}
                                        </button>
                                      )}
                                    </td>
                                  </tr>

                                  {/* Hesaplama formu satırı */}
                                  {hesapAcik && (
                                    <tr>
                                      <td colSpan={8} style={{ padding: '0 12px 12px 12px', background: '#F8FBFF' }}>
                                        <HesaplamaFormu
                                          tahsilat={t}
                                          onKaydet={(alanlar) => hesapKaydet(t.id, alanlar)}
                                          onIptal={() => setAcikHesapId(null)}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* ══ GİDERLER ══ */}
              {sekme === 'gider' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #eee', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#F0F6FF' }}>
                          {['#', 'Açıklama', 'Tutar', 'İşlem'].map((h, i) => (
                            <th key={i} style={{ padding: '11px 14px', textAlign: 'left', color: '#555', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid #E3F0FF' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {giderler.map((g, idx) => (
                          <tr key={g.id} style={{ borderTop: '1px solid #eee' }}>
                            <td style={{ padding: '10px 14px', color: '#999', fontSize: '12px' }}>{idx + 1}</td>
                            <td style={{ padding: '10px 14px' }}>{g.aciklama}</td>
                            <td style={{ padding: '10px 14px', fontWeight: '600', color: '#cc3300' }}>{paraFormat(g.tutar)}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <button onClick={() => giderSil(g.id)} style={{ background: '#fff0f0', color: '#cc0000', border: 'none', padding: '4px 10px', borderRadius: '5px', fontSize: '12px', cursor: 'pointer' }}>Sil</button>
                            </td>
                          </tr>
                        ))}
                        {giderler.length === 0 && (
                          <tr><td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#aaa' }}>Henüz gider yok.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {giderFormAcik ? (
                    <div style={{ background: '#fff', border: '1px solid #c8ddf5', borderRadius: '10px', padding: '16px 20px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px', fontWeight: '500' }}>Açıklama</div>
                        <input autoFocus value={giderForm.aciklama} onChange={e => setGiderForm(f => ({ ...f, aciklama: e.target.value }))} placeholder="Gümrük, nakliye, vb." style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '200px' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px', fontWeight: '500' }}>Tutar (₺)</div>
                        <input type="number" value={giderForm.tutar} onChange={e => setGiderForm(f => ({ ...f, tutar: e.target.value }))} placeholder="0" onKeyDown={e => { if (e.key === 'Enter') giderEkle(); }} style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', width: '110px' }} />
                      </div>
                      <button onClick={giderEkle} style={{ background: '#0D3B7A', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>Kaydet</button>
                      <button onClick={() => { setGiderFormAcik(false); setGiderForm({ aciklama: '', tutar: '' }); }} style={{ background: '#eee', color: '#555', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>İptal</button>
                    </div>
                  ) : (
                    <button onClick={() => setGiderFormAcik(true)} style={{ alignSelf: 'flex-start', background: '#fff', color: '#0D3B7A', border: '1.5px dashed #0D3B7A', padding: '8px 18px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>+ Gider Ekle</button>
                  )}
                </div>
              )}

              {/* ══ SEFER ÖZETİ ══ */}
              <div style={{ background: '#fff', border: '1px solid #c8ddf5', borderRadius: '12px', padding: '16px 24px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0D3B7A', marginBottom: '12px', letterSpacing: '0.3px' }}>
                  SEFER ÖZETİ — {secilenSefer?.sefer_no}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {/* Toplam Alacak */}
                  <div style={{ flex: 1, minWidth: '140px', padding: '10px 18px', borderRight: '1px solid #E3F0FF' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Toplam Alacak</div>
                    {pbSirali.length === 0 ? <div style={{ fontSize: '16px', fontWeight: '700', color: '#bbb' }}>—</div> : pbSirali.map(pb => (
                      <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a' }}>{paraFormatBirim(pbGruplar[pb].alacak, pb)}</div>
                    ))}
                  </div>
                  {/* Tahsil Edilen */}
                  <div style={{ flex: 1, minWidth: '140px', padding: '10px 18px', borderRight: '1px solid #E3F0FF' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tahsil Edilen</div>
                    {pbSirali.length === 0 ? <div style={{ fontSize: '16px', fontWeight: '700', color: '#bbb' }}>—</div> : pbSirali.map(pb => (
                      <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: '#3B6D11' }}>{paraFormatBirim(pbGruplar[pb].odendi, pb)}</div>
                    ))}
                  </div>
                  {/* Kalan Alacak */}
                  <div style={{ flex: 1, minWidth: '140px', padding: '10px 18px', borderRight: '1px solid #E3F0FF' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Kalan Alacak</div>
                    {pbSirali.length === 0 ? <div style={{ fontSize: '16px', fontWeight: '700', color: '#bbb' }}>—</div> : pbSirali.map(pb => {
                      const kalan = pbGruplar[pb].alacak - pbGruplar[pb].odendi;
                      return <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: kalan > 0 ? '#cc3300' : '#3B6D11' }}>{paraFormatBirim(kalan, pb)}</div>;
                    })}
                  </div>
                  {/* Toplam İndirim */}
                  {indirimPbSirali.length > 0 && (
                    <div style={{ flex: 1, minWidth: '140px', padding: '10px 18px', borderRight: '1px solid #E3F0FF' }}>
                      <div style={{ fontSize: '11px', color: '#c45a00', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700' }}>Toplam İndirim</div>
                      {indirimPbSirali.map(pb => (
                        <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: '#c45a00' }}>-{paraFormatBirim(indirimGruplar[pb], pb)}</div>
                      ))}
                    </div>
                  )}
                  {/* Toplam Gider */}
                  <div style={{ flex: 1, minWidth: '140px', padding: '10px 18px', borderRight: '1px solid #E3F0FF' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Toplam Gider</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#854F0B' }}>{paraFormat(toplamGider)}</div>
                  </div>
                  {/* NET */}
                  <div style={{ flex: 1, minWidth: '140px', padding: '10px 18px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NET</div>
                    {tekParaBirimi && pbSirali[0] === 'TL' ? (() => {
                      const net = tlGrup.odendi - toplamGider;
                      return <div style={{ fontSize: '18px', fontWeight: '800', color: net >= 0 ? '#3B6D11' : '#cc0000' }}>{paraFormat(net)}</div>;
                    })() : (
                      <div style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>Karma döviz</div>
                    )}
                  </div>
                </div>

                {kullaniciRol === 'yonetici' && !secilenSefer?.arsivlendi && (
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #eee' }}>
                    <button
                      onClick={arsivlenebilir ? arsivle : undefined}
                      disabled={!arsivlenebilir}
                      style={{
                        background: arsivlenebilir ? '#f5f5f5' : '#fafafa',
                        color: arsivlenebilir ? '#555' : '#bbb',
                        border: '1px solid #ddd', padding: '6px 16px', borderRadius: '6px',
                        fontSize: '12px', cursor: arsivlenebilir ? 'pointer' : 'not-allowed', opacity: arsivlenebilir ? 1 : 0.7,
                      }}
                    >
                      🗄 Seferi Arşivle
                    </button>
                    {!arsivlenebilir && tahsilatlar.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#cc3300', marginTop: '6px' }}>
                        Arşivlemek için tüm ödemelerin tamamlanması gerekiyor. {odenmemisCount} adet ödenmemiş kayıt var.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══ ARŞİV ══ */}
      {anaSecme === 'arsiv' && (
        <div style={{ padding: '20px 28px' }}>
          <div style={{ marginBottom: '16px' }}>
            <input
              value={arsivArama}
              onChange={e => setArsivArama(e.target.value)}
              placeholder="Sefer no veya güzergah ara..."
              style={{ padding: '9px 14px', border: '1px solid #c8ddf5', borderRadius: '8px', fontSize: '14px', background: '#fff', minWidth: '280px', outline: 'none' }}
            />
          </div>

          {arsivYukleniyor ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
          ) : filtreliArsiv.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#aaa', fontSize: '15px' }}>
              {arsivSeferler.length === 0 ? 'Henüz arşivlenmiş sefer yok.' : 'Arama sonucu bulunamadı.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
              {filtreliArsiv.map(s => {
                const oz = arsivOzet[s.id] || { tahsilat: 0, gider: 0 };
                const netKar = oz.tahsilat - oz.gider;
                const netRenk = netKar >= 0 ? '#3B6D11' : '#A32D2D';
                return (
                  <div key={s.id} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e8e8e8', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <div style={{ fontWeight: '700', fontSize: '15px', color: '#0D3B7A' }}>{s.sefer_no}</div>
                      <div style={{ fontSize: '11px', color: '#aaa' }}>Arşiv: {tarihFormat(s.arsiv_tarihi)}</div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>{s.guzergah}</div>
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#555' }}>Toplam Tahsilat</span>
                        <span style={{ fontWeight: '600', color: '#3B6D11' }}>{paraFormat(oz.tahsilat)} ✅</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#555' }}>Toplam Gider</span>
                        <span style={{ fontWeight: '600', color: '#854F0B' }}>-{paraFormat(oz.gider)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '2px' }}>
                        <span style={{ fontWeight: '700', color: '#333' }}>Net Kar</span>
                        <span style={{ fontWeight: '800', color: netRenk }}>{paraFormat(netKar)}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: '14px', textAlign: 'right' }}>
                      <button onClick={() => arsivDetayGor(s)} style={{ background: '#E3F0FF', color: '#0D3B7A', border: 'none', padding: '7px 16px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>Detay Gör →</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ MÜŞTERİ ÖZET MODAL ══ */}
      {musteriModal && (() => {
        const { ad, kalemler } = musteriModal;
        const pbMap = {};
        const indirimMapModal = {};
        kalemler.forEach(t => {
          const pb = t.para_birimi || 'TL';
          if (!pbMap[pb]) pbMap[pb] = { toplam: 0, odendi: 0 };
          pbMap[pb].toplam += parseFloat(t.tahsil_tutari) || 0;
          if (t.durum === 'Ödendi') pbMap[pb].odendi += parseFloat(t.tahsil_tutari) || 0;
          if (parseFloat(t.indirim_tutari) > 0) {
            if (!indirimMapModal[pb]) indirimMapModal[pb] = 0;
            indirimMapModal[pb] += parseFloat(t.indirim_tutari);
          }
        });
        const pbSiraliModal = PARA_BIRIMLERI.filter(pb => pbMap[pb]);
        const indirimPbSiraliModal = PARA_BIRIMLERI.filter(pb => indirimMapModal[pb]);

        const musteriKopyala = () => {
          const satirlar = [`${ad} | ${secilenSefer?.sefer_no || ''}`];
          kalemler.forEach(t => {
            const urun = t._depo?.urun || '—';
            satirlar.push(`${urun}: ${t.notlar || '—'}`);
          });
          const toplamStr = pbSiraliModal.map(pb => `${sayiFormat(pbMap[pb].toplam)}${PB_SEMBOL[pb]}`).join(' + ');
          satirlar.push(`GENEL TOPLAM: ${toplamStr || '—'}`);
          navigator.clipboard.writeText(satirlar.join('\n')).then(() => {
            setModalKopyalandi(true);
            setTimeout(() => setModalKopyalandi(false), 2000);
          });
        };

        return (
          <div onClick={() => setMusteriModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '760px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
              {/* Başlık */}
              <div style={{ padding: '18px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '16px', color: '#0D3B7A' }}>{ad}</div>
                  <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>Sefer Özeti — {secilenSefer?.sefer_no} · {kalemler.length} kalem</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={musteriKopyala}
                    style={{ background: modalKopyalandi ? '#EAF3DE' : '#E3F0FF', color: modalKopyalandi ? '#3B6D11' : '#0D3B7A', border: 'none', padding: '7px 14px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                  >
                    {modalKopyalandi ? '✓ Kopyalandı' : '📋 Paraşüt için Kopyala'}
                  </button>
                  <button onClick={() => setMusteriModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', lineHeight: 1 }}>✕</button>
                </div>
              </div>

              {/* İçerik */}
              <div style={{ overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Kalem tablosu */}
                <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#F0F6FF' }}>
                        {['#', 'Ürün', 'Miktar', 'Gönderici', 'Tutar', 'Durum'].map((h, i) => (
                          <th key={i} style={{ padding: '9px 12px', textAlign: 'left', color: '#555', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid #E3F0FF', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {kalemler.map((t, idx) => {
                        const { bg, renk } = DURUM_RENKLER[t.durum] || DURUM_RENKLER['Ödenmedi'];
                        return (
                          <tr key={t.id} style={{ background: bg, borderTop: '1px solid #eee' }}>
                            <td style={{ padding: '9px 12px', color: '#999', fontSize: '12px' }}>{idx + 1}</td>
                            <td style={{ padding: '9px 12px', fontWeight: '500', color: '#1a1a1a' }}>{t._depo?.urun || '—'}</td>
                            <td style={{ padding: '9px 12px', color: '#555', fontSize: '12px' }}>{t._depo?.miktar || '—'}</td>
                            <td style={{ padding: '9px 12px', color: '#555', fontSize: '12px' }}>{t._depo?.gonderici_firma || '—'}</td>
                            <td style={{ padding: '9px 12px', fontWeight: '700', color: t.tahsil_tutari ? '#0D3B7A' : '#bbb' }}>
                              {t.tahsil_tutari != null ? paraFormatBirim(t.tahsil_tutari, t.para_birimi) : '—'}
                              {parseFloat(t.indirim_tutari) > 0 && (
                                <div style={{ fontSize: '11px', color: '#c45a00', fontWeight: '600', marginTop: '2px' }}>
                                  -{paraFormatBirim(t.indirim_tutari, t.para_birimi)} indirim
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '9px 12px' }}>
                              <span style={{ background: bg, color: renk, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', border: `1px solid ${renk}33` }}>{t.durum}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Özet */}
                <div style={{ background: '#F8FBFF', border: '1px solid #c8ddf5', borderRadius: '10px', padding: '14px 20px', display: 'flex', flexWrap: 'wrap', gap: '0' }}>
                  {[
                    {
                      etiket: 'Toplam Kalem',
                      icerik: <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a' }}>{kalemler.length}</div>,
                    },
                    {
                      etiket: 'Toplam Tutar',
                      icerik: pbSiraliModal.length === 0
                        ? <div style={{ fontSize: '16px', fontWeight: '700', color: '#bbb' }}>—</div>
                        : pbSiraliModal.map(pb => <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a' }}>{paraFormatBirim(pbMap[pb].toplam, pb)}</div>),
                    },
                    {
                      etiket: 'Ödenen',
                      icerik: pbSiraliModal.length === 0
                        ? <div style={{ fontSize: '16px', fontWeight: '700', color: '#bbb' }}>—</div>
                        : pbSiraliModal.map(pb => <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: '#3B6D11' }}>{paraFormatBirim(pbMap[pb].odendi, pb)}</div>),
                    },
                    {
                      etiket: 'Kalan',
                      icerik: pbSiraliModal.length === 0
                        ? <div style={{ fontSize: '16px', fontWeight: '700', color: '#bbb' }}>—</div>
                        : pbSiraliModal.map(pb => {
                            const kalan = pbMap[pb].toplam - pbMap[pb].odendi;
                            return <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: kalan > 0 ? '#cc3300' : '#3B6D11' }}>{paraFormatBirim(kalan, pb)}</div>;
                          }),
                    },
                    ...(indirimPbSiraliModal.length > 0 ? [{
                      etiket: 'Toplam İndirim',
                      icerik: indirimPbSiraliModal.map(pb => (
                        <div key={pb} style={{ fontSize: '15px', fontWeight: '700', color: '#c45a00' }}>-{paraFormatBirim(indirimMapModal[pb], pb)}</div>
                      )),
                      _orange: true,
                    }] : []),
                  ].map((k, i, arr) => (
                    <div key={i} style={{ flex: 1, minWidth: '130px', padding: '8px 16px', borderRight: i < arr.length - 1 ? '1px solid #E3F0FF' : 'none' }}>
                      <div style={{ fontSize: '11px', color: k._orange ? '#c45a00' : '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: k._orange ? '700' : undefined }}>{k.etiket}</div>
                      {k.icerik}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ ARŞİV DETAY MODAL ══ */}
      {arsivDetayModal && (
        <div onClick={() => setArsivDetayModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '900px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#0D3B7A' }}>🗄 {arsivDetayModal.sefer_no} — {arsivDetayModal.guzergah}</div>
                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>Arşivlenme: {tarihFormat(arsivDetayModal.arsiv_tarihi)} · Salt okunur</div>
              </div>
              <button onClick={() => setArsivDetayModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {arsivDetayYukleniyor ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
              ) : (
                <>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tahsilat</div>
                    <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#F0F6FF' }}>
                            {['#', 'Alıcı', 'Ürün', 'Fatura No', 'Tahsil Tutarı', 'Not', 'Durum'].map((h, i) => (
                              <th key={i} style={{ padding: '9px 12px', textAlign: 'left', color: '#555', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid #E3F0FF', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {arsivDetayTahsilat.map((t, idx) => {
                            const { bg, renk } = DURUM_RENKLER[t.durum] || DURUM_RENKLER['Ödenmedi'];
                            return (
                              <tr key={t.id} style={{ background: bg, borderTop: '1px solid #eee' }}>
                                <td style={{ padding: '8px 12px', color: '#999', fontSize: '12px' }}>{idx + 1}</td>
                                <td style={{ padding: '8px 12px', fontWeight: '600', color: '#1a1a1a' }}>{t._depo?.musteriler?.ad || '—'}</td>
                                <td style={{ padding: '8px 12px', color: '#555' }}>{t._depo?.urun || '—'}</td>
                                <td style={{ padding: '8px 12px', color: '#888', fontSize: '12px' }}>{t._depo?.fatura_no || '—'}</td>
                                <td style={{ padding: '8px 12px', fontWeight: '600', color: '#0D3B7A' }}>{t.tahsil_tutari != null ? paraFormat(t.tahsil_tutari) : '—'}</td>
                                <td style={{ padding: '8px 12px', color: '#555', fontSize: '11px', maxWidth: '200px' }}>{t.notlar || '—'}</td>
                                <td style={{ padding: '8px 12px' }}>
                                  <span style={{ background: bg, color: renk, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', border: `1px solid ${renk}22` }}>{t.durum}</span>
                                </td>
                              </tr>
                            );
                          })}
                          {arsivDetayTahsilat.length === 0 && (
                            <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Tahsilat kaydı yok.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Giderler</div>
                    <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#F0F6FF' }}>
                            {['#', 'Açıklama', 'Tutar'].map((h, i) => (
                              <th key={i} style={{ padding: '9px 12px', textAlign: 'left', color: '#555', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid #E3F0FF' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {arsivDetayGider.map((g, idx) => (
                            <tr key={g.id} style={{ borderTop: '1px solid #eee' }}>
                              <td style={{ padding: '8px 12px', color: '#999', fontSize: '12px' }}>{idx + 1}</td>
                              <td style={{ padding: '8px 12px' }}>{g.aciklama}</td>
                              <td style={{ padding: '8px 12px', fontWeight: '600', color: '#cc3300' }}>{paraFormat(g.tutar)}</td>
                            </tr>
                          ))}
                          {arsivDetayGider.length === 0 && (
                            <tr><td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>Gider kaydı yok.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {(() => {
                    const dTahsil = arsivDetayTahsilat.filter(t => t.durum === 'Ödendi').reduce((s, t) => s + (parseFloat(t.tahsil_tutari) || 0), 0);
                    const dGider  = arsivDetayGider.reduce((s, g) => s + (parseFloat(g.tutar) || 0), 0);
                    const dNet    = dTahsil - dGider;
                    return (
                      <div style={{ background: '#F8FBFF', border: '1px solid #c8ddf5', borderRadius: '10px', padding: '14px 20px', display: 'flex', flexWrap: 'wrap' }}>
                        {[
                          { etiket: 'Toplam Tahsilat', deger: paraFormat(dTahsil), renk: '#3B6D11' },
                          { etiket: 'Toplam Gider',    deger: paraFormat(dGider),  renk: '#854F0B' },
                          { etiket: 'NET',             deger: paraFormat(dNet),    renk: dNet >= 0 ? '#3B6D11' : '#A32D2D', bold: true },
                        ].map((k, i, arr) => (
                          <div key={i} style={{ flex: 1, minWidth: '140px', padding: '8px 16px', borderRight: i < arr.length - 1 ? '1px solid #E3F0FF' : 'none' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k.etiket}</div>
                            <div style={{ fontSize: k.bold ? '17px' : '15px', fontWeight: k.bold ? '800' : '700', color: k.renk }}>{k.deger}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {kullaniciRol === 'yonetici' && (
                    <div>
                      <button onClick={() => arsivdenCikar(arsivDetayModal.id)} style={{ background: '#fff0f0', color: '#A32D2D', border: '1px solid #f5c6c6', padding: '7px 18px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>
                        ↩ Arşivden Çıkar
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
